// frontend/src/components/forms/AssetForm.tsx
import React, { useState, FormEvent } from 'react';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';

// Asume que la URL base está disponible globalmente o la defines aquí/importas
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface AssetFormProps {
  onAssetCreated?: () => void; // Callback opcional para ejecutar después de crear
}

export function AssetForm({ onAssetCreated }: AssetFormProps) {
  const [name, setName] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [category, setCategory] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    // Creamos el objeto SÓLO con los datos que el formulario maneja activamente
    // Si el campo 'status' estuviera activo, se incluiría aquí.
    const assetData = { name, identifier, category };

    try {
      const response = await fetch(`${API_BASE_URL}/api/data/assets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(assetData),
      });

      // Es mejor leer el resultado aquí por si hay errores que vienen en JSON
      const result = await response.json();

      if (!response.ok) {
        // Usar el mensaje de error del backend si existe, sino uno genérico
        throw new Error(result.error || `Error del servidor: ${response.statusText}`);
      }

      // Usar datos del resultado devuelto por el backend (más fiable)
      setSuccess(`Activo "${result.name}" (ID: ${result.id}) creado exitosamente.`);
      // Limpiar formulario
      setName('');
      setIdentifier('');
      setCategory('');
      // setStatus('Operativo'); // Resetear si el campo estuviera activo

      // Llamar al callback si existe (ej. para refrescar una lista)
      onAssetCreated?.();

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ocurrió un error desconocido al crear el activo.');
      console.error("Error en AssetForm submit:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <h4>Registrar Nuevo Activo</h4>
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}

      <Form.Group className="mb-3" controlId="assetName">
        <Form.Label>Nombre Descriptivo</Form.Label>
        <Form.Control
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="Ej: Camión Tolva 1, Excavadora Oficina"
          disabled={isLoading}
        />
      </Form.Group>

      <Form.Group className="mb-3" controlId="assetIdentifier">
        <Form.Label>Identificador Único</Form.Label>
        <Form.Control
          type="text"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          required
          placeholder="Ej: ABCD12, Nro Interno 03"
          disabled={isLoading}
        />
        <Form.Text className="text-muted">
          Patente, número de serie o número interno. No se debe repetir.
        </Form.Text>
      </Form.Group>

      <Form.Group className="mb-3" controlId="assetCategory">
        <Form.Label>Categoría</Form.Label>
        <Form.Control
          type="text"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder="Ej: Vehículo Pesado, Maquinaria"
          disabled={isLoading}
        />
      </Form.Group>

      {/* El campo de Estado sigue comentado como en tu versión. Descomenta si quieres usarlo. */}
      {/* <Form.Group className="mb-3" controlId="assetStatus">
         <Form.Label>Estado Inicial</Form.Label>
         <Form.Select value={status} onChange={(e) => setStatus(e.target.value)} disabled={isLoading}>
           <option value="Operativo">Operativo</option>
           <option value="En Taller">En Taller</option>
           <option value="Requiere Revisión">Requiere Revisión</option>
         </Form.Select>
      </Form.Group> */}

      <Button variant="primary" type="submit" disabled={isLoading}>
        {isLoading ? 'Registrando...' : 'Registrar Activo'}
      </Button>
    </Form>
  );
}